import type { PipeSize1 } from '../../../../types/setup';
import { convertValueToMm } from '../../../../../../utils/unitUtils';

export const MAX_QR_PAYLOAD_BYTES = 2900;

export type PipeQRPayloadPipe = {
  pipeId: number;
  /** Pipe OD in inches as stored — no unit conversion. */
  pipeSize: number;
  pipeType: string;
  pipeMode: string;
  pipeSequence: string;
  /** Inner diameter in mm */
  pipeInnerDiameter: number;
  /** Wall thickness in mm (2 decimal places). */
  wallThickness: number;
  groupId: string;
  pipeMaterial: string;
  pipeLength: number;
  toeSeamSeparationMinMm: number;
  seamCapWidthMaxMm: number;
  sixOclockExclusionDeg: number;
  /** HiLo threshold in mm as stored — no unit conversion. */
  hiloThreshold: number;
  hiloWpsLimitMm: number;
  /** Nominal OD in mm as stored — no unit conversion. */
  nominalOd?: number;
  targetNumberOfPipes?: number;
  excessiveHiloLengthLimitDeg: number;
  hiloPreferredLimitTopMm: number;
  hiloPreferredLimitMiddleMm: number;
  hiloPreferredLimitLowerMm: number;
};

export type PipeQRPayload = {
  projectNumber: string;
  projectName: string;
  customerName?: string;
  location?: string;
  pipes: PipeQRPayloadPipe[];
};

const dash = (v: unknown) => {
  if (v == null) return '-';
  const s = String(v).trim();
  return s === '' ? '-' : s;
};

/** Format numeric QR values to 3 decimal places when finite (Pipe OD inches). */
const dash3dp = (v: unknown) => {
  if (v == null || v === '') return '-';
  const n = Number(v);
  if (!Number.isFinite(n)) return dash(v);
  return n.toFixed(3);
};

/** Format numeric QR values to 2 decimal places when finite. */
const dash2dp = (v: unknown) => {
  if (v == null || v === '') return '-';
  const n = Number(v);
  if (!Number.isFinite(n)) return dash(v);
  return n.toFixed(2);
};

/**
 * Slim plain-text QR (project-creation style).
 * Pipe OD = inches as stored (no convert). Wall thickness = mm, 2 dp.
 */
export function formatPipeQRText(payload: PipeQRPayload): string {
  const lines: string[] = [
    `Project Number : ${dash(payload.projectNumber)}`,
    `Project Name : ${dash(payload.projectName)}`,
    `Customer Name : ${dash(payload.customerName)}`,
  ];

  payload.pipes.forEach((pipe, i) => {
    if (payload.pipes.length > 1) {
      lines.push(`Pipe ${i + 1}`);
    }
    lines.push(
      `Group ID : ${dash(pipe.groupId)}`,
      `Pipe Length : ${dash(pipe.pipeLength)}`,
      `Pipe OD : ${dash3dp(pipe.pipeSize)}`,
      `Wall Thickness (mm) : ${dash2dp(pipe.wallThickness)}`,
      `Pipe Material : ${dash(pipe.pipeMaterial)}`
    );
  });

  lines.push(`Location : ${dash(payload.location)}`);

  // Leading spaces match project-creation QR (`createQrCodeDataForProject`).
  return `\n${lines.map((l) => `  ${l}`).join('\n')} `;
}

/**
 * Structured payload for email PDF / UI; QR image encodes {@link formatPipeQRText}.
 * When pipeIndex is set, only that pipe is included.
 */
export function buildPipeQRPayload(options: {
  projectNumber?: string | null;
  projectName?: string | null;
  customerName?: string | null;
  location?: string | null;
  pipeSizes: PipeSize1[];
  pipeIndex?: number;
}): PipeQRPayload {
  const projectNumber = options.projectNumber ?? '';
  const projectName = options.projectName ?? '';
  const customerName = options.customerName ?? '';
  const location = options.location ?? '';
  const { pipeSizes, pipeIndex } = options;

  const num = (v: unknown) => (v != null && v !== '' ? Number(v) : 0);
  const str = (v: unknown) =>
    v != null && String(v).trim() !== '' ? String(v).trim() : '';
  const seq = (v: unknown) => {
    const s = v != null ? String(v).trim() : '';
    return s === 'Sequence' || s === 'Individual' ? s : 'Sequence';
  };
  const round3 = (v: number) => Math.round(v * 1000) / 1000;
  const round2 = (v: number) => Math.round(v * 100) / 100;

  const mapPipe = (p: PipeSize1, index: number): PipeQRPayloadPipe => {
    const fieldUnits: Record<string, string> = p.units?.pipefitterFieldUnits ?? {};
    const wallUnit = p.units?.wallThickness || fieldUnits.wallThickness || 'in';
    const idUnit =
      fieldUnits.pipeInnerDiameter || p.units?.pipeSize || fieldUnits.pipeSize || 'in';
    const toMmFromUnit = (v: unknown, unit: string) => {
      const raw = num(v);
      const inMm = unit === 'mm' ? raw : convertValueToMm(raw, unit);
      return Math.round(inMm * 10000) / 10000;
    };
    // Pipe OD: inches as stored — do not convert.
    const pipeOdIn = round3(num(p.pipeSize));
    // Wall thickness: mm, 2 decimal places.
    const wallMm = round2(
      wallUnit === 'mm'
        ? num(p.wallThickness)
        : convertValueToMm(num(p.wallThickness), wallUnit)
    );
    // Pipe length: stored display value — do not convert to mm.
    const pipeLengthStored = round2(num(p.pipeLength));
    // HiLo / related *Mm fields are already mm — do not convert.
    const asMm = (v: unknown) => round2(num(v));
    return {
      pipeId: p.pipeId != null ? Number(p.pipeId) : index + 1,
      pipeSize: pipeOdIn,
      pipeType: str(p.pipeType) || 'Seam',
      pipeMode: str(p.pipeMode) || 'Single Joint',
      pipeSequence: seq(p.pipeSequence),
      pipeInnerDiameter: toMmFromUnit(p.pipeInnerDiameter, idUnit),
      wallThickness: wallMm,
      groupId: str(p.groupId),
      pipeMaterial: str(p.pipeMaterial),
      pipeLength: pipeLengthStored,
      toeSeamSeparationMinMm: asMm(p.toeSeamSeparationMinMm),
      seamCapWidthMaxMm: asMm(p.seamCapWidthMaxMm),
      sixOclockExclusionDeg: num(p.sixOclockExclusionDeg),
      hiloThreshold: asMm(p.hiloThreshold),
      hiloWpsLimitMm: asMm(p.hiloWpsLimitMm),
      nominalOd: asMm(
        p.nominalOd ?? p.nominalOdMax ?? p.nominalOdMin
      ),
      targetNumberOfPipes: num(p.targetNumberOfPipes) || undefined,
      excessiveHiloLengthLimitDeg: num(p.excessiveHiloLengthLimitDeg),
      hiloPreferredLimitTopMm: asMm(p.hiloPreferredLimitTopMm),
      hiloPreferredLimitMiddleMm: asMm(p.hiloPreferredLimitMiddleMm),
      hiloPreferredLimitLowerMm: asMm(p.hiloPreferredLimitLowerMm),
    };
  };

  const pipes =
    pipeIndex != null && pipeIndex >= 0 && pipeIndex < pipeSizes.length
      ? [mapPipe(pipeSizes[pipeIndex], pipeIndex)]
      : pipeSizes.map(mapPipe);

  return { projectNumber, projectName, customerName, location, pipes };
}
